import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Modal from '../ui/Modal';
import { CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Label from '../ui/Label';
import Textarea from '../ui/Textarea';
import { CriteriaSentence } from './criteriaText';
import { segmentTemplatesService } from '../../services/segmentTemplatesService';

/** Save the current criteria tree as a reusable template for this organization. */
const SaveTemplateDialog = ({ isOpen, onClose, criteria, defaultName = '', organizationId, organizationName = '' }) => {
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setDescription('');
    }
  }, [isOpen, defaultName]);

  const save = async () => {
    if (!name.trim()) {
      toast.error('Give the template a name');
      return;
    }
    setSaving(true);
    try {
      await segmentTemplatesService.create({
        name: name.trim(),
        description: description.trim(),
        criteria,
        organization_id: parseInt(organizationId, 10),
      });
      toast.success('Template saved');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} onEnter={save} className="w-full max-w-lg">
      <CardHeader className="pr-10">
        <CardTitle className="text-lg text-[#18181B]">Save as template</CardTitle>
        <p className="text-sm text-zinc-500">
          {organizationName
            ? `This template will be available to ${organizationName} as a starting point.`
            : 'This template will be available to this organization as a starting point.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="tpl-name">Name</Label>
          <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Active Gmail users, last 30 days" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tpl-desc">Description (optional)</Label>
          <Textarea id="tpl-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="When to use this template" className="min-h-[60px]" />
        </div>
        <p className="rounded-lg bg-[#F8FAFC] px-3 py-2 text-sm">
          <CriteriaSentence criteria={criteria} />
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 rounded-md px-4 text-sm text-zinc-600 hover:bg-[#E8EEF5]">
            Cancel
          </button>
          <Button variant="outline" onClick={save} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save template
          </Button>
        </div>
      </CardContent>
    </Modal>
  );
};

export default SaveTemplateDialog;
